import * as React from "react";
import {useMemo, useState} from "react";
import {Button, Carousel, Col, ProgressBar, Row} from "react-bootstrap";
import {SESSION_DURATION_MINUTES} from "../utils/constants";
import moment from "moment";
import {formatNumber} from "../utils/commons.ts";
import {StatsLoader} from "./StatsLoader";
import {Feature, hasFeature} from "../utils/features";
import {HelpModalIcon, IMAGES, KButton, KBUTTON_SIZE, KBUTTON_VARIANT, KCard, KSpace, Translate} from "@kopjra/uikit";
import tip1 from "../resources/imgs/WebForensics_WelcomeScreen_Screenshots_Tip1.jpg";
import tip3 from "../resources/imgs/WebForensics_WelcomeScreen_Screenshots_Tip3.jpg";
import {useAuth0} from "@auth0/auth0-react";
import {useGetStatsQuery, useGetUserInfo} from "../api/user.ts";
import {getUserFullName} from "../types/userstate.ts";
import {Routes} from "../utils/router.tsx";

export const Stats: React.FC = () => {
    const {user} = useAuth0();

    const {data: userInfo} = useGetUserInfo();
    const {data: stats} = useGetStatsQuery(undefined, {pollingInterval: 10000});
    const fullName = useMemo(() => getUserFullName(userInfo), [userInfo]);
    const storeUrl = userInfo?.storeUrl;

    const [activeIndex, setActiveIndex] = useState(0);
    const [show, setShow] = useState<boolean | undefined>();
    let remainingTimeStr = "";
    if (stats && stats.remaining > 0) {
        const minutes = stats.remaining * SESSION_DURATION_MINUTES;
        const duration = moment.duration(minutes, "minutes");
        remainingTimeStr = `-${formatNumber(Math.floor(duration.asHours()))}:${formatNumber(duration.minutes())}:${formatNumber(duration.seconds())}`;
    }

    return (
        <KCard header={<Translate i18nKey="stats.title"/>} helpModal={{
            show,
            onShow: (s) => setShow(s),
            content: (
                <Carousel interval={null} fade={true} className="welcome text-center" activeIndex={activeIndex} onSelect={(selected) => setActiveIndex(selected)}>
                    <Carousel.Item>
                        <div style={{display: "block"}}>
                            <KSpace spaces={5}/>
                            <img width={125} src={IMAGES["web-forensics"].still.large.dark} alt={"wf"}/>
                            <KSpace spaces={2}/>
                            <h1><Translate i18nKey="home.welcome.title"/>{fullName ? `, ${fullName}` : ""}</h1>
                            <h3><Translate i18nKey={"home.welcome.subtitle"}/></h3>
                            <KSpace spaces={7}/>
                            <Row>
                                <Col md={4}><KButton text={<Translate i18nKey={"home.welcome.skip"}/>} fill={true} variant={KBUTTON_VARIANT.secondary} onClick={() => setShow(false)}/></Col>
                                <Col md={4}><KButton text={<Translate i18nKey={"home.welcome.next"}/>} fill={true} variant={KBUTTON_VARIANT.primary} onClick={() => setActiveIndex(activeIndex + 1)}/></Col>
                            </Row>
                            <KSpace spaces={2}/>
                        </div>
                    </Carousel.Item>
                    <Carousel.Item>
                        <img width={560} className={"tip"} src={tip1} alt={"dash1"}/>
                        <KSpace spaces={2}/>
                        <p><Translate i18nKey={"home.welcome.tip1"}/></p>
                        <KSpace spaces={2}/>
                        <Row>
                            <Col md={4}><KButton text={<Translate i18nKey={"home.welcome.skip"}/>} fill={true} variant={KBUTTON_VARIANT.secondary} onClick={() => setShow(false)}/></Col>
                            <Col md={4}><KButton text={<Translate i18nKey={"home.welcome.next"}/>} fill={true} variant={KBUTTON_VARIANT.primary} onClick={() => setActiveIndex(activeIndex + 1)}/></Col>
                        </Row>
                        <KSpace spaces={2}/>
                    </Carousel.Item>
                    <Carousel.Item>
                        <div className="iframe-container">
                            <iframe width="560" height="310" src="https://www.youtube.com/embed/omk68q56CgM?si=6QHQOBupLe3asceK?color=white&amp;modestbranding=1&amp;rel=0&amp;controls=0" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"></iframe>
                        </div>
                        <KSpace spaces={2}/>
                        <p><Translate i18nKey={"home.welcome.tip2"}/></p>
                        <KSpace spaces={2}/>
                        <Row>
                            <Col md={4}><KButton text={<Translate i18nKey={"home.welcome.skip"}/>} fill={true} variant={KBUTTON_VARIANT.secondary} onClick={() => setShow(false)}/></Col>
                            <Col md={4}><KButton text={<Translate i18nKey={"home.welcome.next"}/>} fill={true} variant={KBUTTON_VARIANT.primary} onClick={() => setActiveIndex(activeIndex + 1)}/></Col>
                        </Row>
                        <KSpace spaces={2}/>
                    </Carousel.Item>
                    <Carousel.Item>
                        <img width={560} className={"tip"} src={tip3} alt={"dash1"}/>
                        <KSpace spaces={2}/>
                        <p><Translate i18nKey={"home.welcome.tip3"}/></p>
                        <KSpace spaces={2}/>
                        <Row>
                            <Col md={4}><KButton text={<Translate i18nKey={"home.welcome.gotodash"}/>} fill={true} variant={KBUTTON_VARIANT.primary} onClick={() => setShow(false)}/></Col>
                        </Row>
                        <KSpace spaces={2}/>
                    </Carousel.Item>
                </Carousel>
            ),
            tooltip: <Translate i18nKey="home.welcome.tooltip"/>,
            icon: HelpModalIcon.INFO,
        }}>
            {(stats) ? (
                <>
                    <KSpace spaces={2}/>
                    {(stats.remaining === 0 && stats.used === 0 && stats.completed === 0 && !stats.infinite) ? (
                        <Row>
                            <Col className="landing text-center">
                                <img src={IMAGES["web-forensics"].still.large.dark} height={120} alt={"Web Forensics"}/>
                                <span className={"appName"}>Web Forensics</span>
                                {/*<h4 style={{paddingTop: 20}}><Translate i18nKey="home.payoff"/></h4>*/}
                                <h4 className="call" style={{paddingTop: 50}}><Translate i18nKey="home.call"/></h4>
                                <div style={{paddingTop: 50}}>
                                    <Button
                                        variant={"primary"}
                                        onClick={() => {
                                            if (user?.partner === "opendotcom") {
                                                window.open("https://www.opendotcom.it/digital-forensics/acquista.aspx", "_blank");
                                            } else {
                                                window.open("https://store.kopjra.com", "_blank");
                                            }
                                        }}
                                    >
                                        <i className="fal fa-shopping-cart"/> <Translate i18nKey="bar.buy"/>
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    ) : (
                        <>
                            <Row>
                                {(!stats.infinite) ? (
                                    <>
                                        <Col className="text-center" md={{span: 3}} xs={4}>
                                            {(stats.remaining > 0) ? (
                                                <span className="bigNumber text-primary">{stats.remaining}</span>
                                            ) : (
                                                <span className="bigNumber text-danger">{stats.remaining}</span>
                                            )}
                                        </Col>
                                        <Col className="text-center" md={{span: 3}} xs={4}>
                                            <span className="bigNumber text-secondary">{stats.used}</span>
                                        </Col>
                                    </>
                                ) : (
                                    <Col className="text-center" md={{span: 3}} xs={8}>
                                        <span className="bigNumber text-primary"><i className="fa fa-infinity"/></span>
                                    </Col>
                                )}
                                <Col className="text-center" md={{span: 3}} xs={4}>
                                    <span className="bigNumber text-success">{stats.completed}</span>
                                </Col>
                            </Row>
                            <Row style={{marginTop: -10}}>
                                {(!stats.infinite) ? (
                                    <>
                                        <Col className="text-center" md={{span: 3}} xs={4}>
                                            <span className="sessionLabel"><Translate i18nKey="stats.remaining"/></span>
                                        </Col>
                                        <Col className="text-center" md={{span: 3}} xs={4}>
                                            <span className="sessionLabel"><Translate i18nKey="stats.used"/></span>
                                        </Col>
                                    </>
                                ) : (
                                    <Col className="text-center" md={{span: 3}} xs={8}>
                                        <span className="sessionLabel"><Translate i18nKey="stats.remaining"/></span>
                                    </Col>
                                )}
                                <Col className="text-center" md={{span: 3}} xs={4}>
                                    <span className="sessionLabel"><Translate i18nKey="stats.completed"/></span>
                                </Col>
                            </Row>
                            {(stats.remaining > 0 || stats.infinite) ? (
                                <>
                                    {(!stats.infinite) ? (
                                        <Row style={{marginTop: 50}}>
                                            <Col className="text-end" md={{span: 3}} xs={4}>
                                                <span className="sessionLabel"><Translate i18nKey="stats.remainingtime"/></span>
                                            </Col>
                                            <Col className="text-center" md={{span: 4}} xs={4} style={{paddingTop: 12}}>
                                                <span className="sessionLabel"><ProgressBar now={(stats!.used / (stats!.remaining + stats!.used))*100}/></span>
                                            </Col>
                                            <Col className="text-start" md={{span: 3}} xs={4}>
                                                <span className="sessionLabel">{remainingTimeStr}</span>
                                            </Col>
                                        </Row>
                                    ) : null }
                                    <Row style={{marginTop: 60}}>
                                        <Col md={{span: 4}} xs={12}>
                                            <KButton text={<><i className="fal fa-lg fa-plus"/>&nbsp;&nbsp;<Translate i18nKey="bar.createbutton"/></>}
                                                     variant={KBUTTON_VARIANT.success}
                                                     onClick={() => Routes.goNew_session()}
                                                     size={KBUTTON_SIZE.lg}
                                                     fill={true}
                                            />
                                        </Col>
                                    </Row>
                                    {(!stats.infinite) ? (
                                        <Row style={{marginTop: 20, marginBottom: 40}}>
                                            <Col className="text-center">
                                                {/*<span className="normalAnchor" style={{cursor: "pointer"}} onClick={() => onGetStoreUrl()}><Translate i18nKey="stats.more"/></span>*/}
                                                {hasFeature(Feature.GFL) ? (
                                                    <a className="normalAnchor" style={{cursor: "pointer"}} href={storeUrl || ""} target="_blank" rel="noopener noreferrer"><Translate i18nKey="stats.more"/></a>
                                                ) : (
                                                    <a className="normalAnchor" style={{cursor: "pointer"}} href={user?.partner === "opendotcom" ? "https://www.opendotcom.it/digital-forensics/acquista.aspx" : "https://store.kopjra.com"} target="_blank" rel="noopener noreferrer"><Translate i18nKey="stats.moreWF"/></a>
                                                )}

                                            </Col>
                                        </Row>
                                    ) : (
                                        <Row style={{marginTop: 20, marginBottom: 40}}/>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Row style={{marginTop: 50}}>
                                        <Col className="text-center">
                                            <span className="normalText"><Translate i18nKey="stats.nomorepart1"/></span>&nbsp;
                                            {/*<span className="normalAnchor" style={{cursor: "pointer"}} onClick={() => onGetStoreUrl()}><Translate i18nKey="stats.nomorepart2"/></span>&nbsp;*/}
                                            <a className="normalAnchor" style={{cursor: "pointer"}} href={user?.partner === "opendotcom" ? "https://www.opendotcom.it/digital-forensics/acquista.aspx" : storeUrl || "https://store.kopjra.com"} target="_blank" rel="noopener noreferrer" ><Translate i18nKey="stats.nomorepart2"/></a>&nbsp;
                                            <span className="normalText"><Translate i18nKey="stats.nomorepart3"/></span>
                                        </Col>
                                    </Row>
                                    <Row style={{marginTop: 40}}>
                                        <Col md={{span: 4}} xs={12}>
                                            <KButton text={<><i className="fal fa-lg fa-shopping-cart"/>&nbsp;&nbsp;<Translate i18nKey="stats.buy"/></>}
                                                     variant={KBUTTON_VARIANT.primary}
                                                     size={KBUTTON_SIZE.lg}
                                                     fill={true}
                                                     onClick={() => {
                                                         if (user?.partner === "opendotcom") {
                                                             window.open("https://www.opendotcom.it/digital-forensics/acquista.aspx", "_blank");
                                                         } else {
                                                             window.open(storeUrl || "https://store.kopjra.com", "_blank");
                                                         }
                                                     }}
                                            />
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </>
                    )}
                </>
            ): (
                <Row style={{marginTop: 50, marginBottom: 50}}>
                    <Col className="text-center">
                        <StatsLoader/>
                    </Col>
                </Row>
            )}
        </KCard>
    );
};
